/**
 * Reminders from chat (M5): "remind me …" / "ذكّرني …" are handled without the model. The
 * confirmation is one line, "Noted. Thursday 9:00." / "تم. الخميس 9:00.", with a 👍 reaction.
 */
import { type Lang, localDate } from './calendar-window';
import { latinDigits, parseWhen, type ParsedWhen } from './time-parse';

export interface DetectedReminder {
  /** What to say when the time comes, as the person phrased it (without the time words). */
  what: string;
  when: ParsedWhen;
  lang: Lang;
}

const EN_TRIGGER = /^\s*(?:please\s+|can you\s+|could you\s+)?remind me\b/iu;
// \b is ASCII-only in JS regexes: the Arabic trigger ends on a non-Arabic-letter lookahead.
const AR_TRIGGER =
  /^\s*(?:من فضلك\s+|لو سمحت\s+)?(?:ذكّرني|ذكرني|ذكريني|فكّرني|فكرني)(?![\u0600-\u06FF])/u;

/** True when the message asks for a reminder (either language). */
export function isReminderRequest(text: string): boolean {
  return EN_TRIGGER.test(text) || AR_TRIGGER.test(text);
}

/**
 * Deterministic parse of a reminder request. Undefined when the message is not a reminder
 * request or carries no time (the model then handles it with the reminder tool).
 */
export function detectReminder(
  text: string,
  opts: { now: Date; timezone: string },
): DetectedReminder | undefined {
  const raw = text.trim();
  const lang: Lang = AR_TRIGGER.test(raw) ? 'ar' : EN_TRIGGER.test(raw) ? 'en' : 'en';
  if (!isReminderRequest(raw)) return undefined;
  const when = parseWhen(raw, opts);
  if (!when) return undefined;
  const what = reminderSubject(raw, when.matched, lang);
  if (!what) return undefined;
  return { what, when, lang };
}

/** The reminder text without the trigger, the time words and connectors. */
export function reminderSubject(text: string, timeWords: string, lang: Lang): string {
  let s = latinDigits(text).replace(/\s+/g, ' ').trim();
  s = s.replace(EN_TRIGGER, '').replace(AR_TRIGGER, '');
  if (timeWords) {
    const escaped = timeWords.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
    s = s.replace(new RegExp(escaped, 'iu'), ' ');
  }
  s = s
    .replace(/\b(?:to|that|about|of)\b\s*/iu, '')
    .replace(/^\s*(?:أن|بأن|ب|عن|بـ|إني|أني|اني)\s+/u, '')
    .replace(/\s+(?:at|on|in|by|next|this|coming)\s*$/iu, '')
    .replace(/\s+(?:يوم|في|الساعة|عند|بعد|خلال)\s*$/u, '')
    .replace(/[\s,،.]+$/u, '')
    .replace(/^[\s,،.]+/u, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (lang === 'ar') s = s.replace(/^ب(?=[؀-ۿ])/u, '');
  return s;
}

const WEEKDAY_NAMES: Record<Lang, string[]> = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
};

const MONTH_NAMES: Record<Lang, string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  ar: [
    'يناير',
    'فبراير',
    'مارس',
    'أبريل',
    'مايو',
    'يونيو',
    'يوليو',
    'أغسطس',
    'سبتمبر',
    'أكتوبر',
    'نوفمبر',
    'ديسمبر',
  ],
};

/**
 * The day and time as the confirmation shows them: "Today 14:20", "Tomorrow 9:00",
 * "Thursday 9:00" within the week, else "3 Oct 9:00" (Arabic: "اليوم", "غداً", "الخميس", "3 أكتوبر").
 */
export function whenLabel(at: Date, opts: { now: Date; timezone: string; lang: Lang }): string {
  const { now, timezone, lang } = opts;
  const a = localDate(at, timezone);
  const n = localDate(now, timezone);
  const time = `${a.hour}:${String(a.minute).padStart(2, '0')}`;
  const dayDiff = Math.round(
    (Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(n.year, n.month - 1, n.day)) / 86_400_000,
  );
  let day: string;
  if (dayDiff === 0) day = lang === 'ar' ? 'اليوم' : 'Today';
  else if (dayDiff === 1) day = lang === 'ar' ? 'غداً' : 'Tomorrow';
  else if (dayDiff > 1 && dayDiff < 7) day = WEEKDAY_NAMES[lang][a.weekday] ?? '';
  else day = `${a.day} ${MONTH_NAMES[lang][a.month - 1] ?? ''}`;
  return `${day} ${time}`;
}

/** "Noted. Thursday 9:00." / "تم. الخميس 9:00." */
export function reminderConfirmation(
  at: Date,
  opts: { now: Date; timezone: string; lang: Lang },
): string {
  const label = whenLabel(at, opts);
  return opts.lang === 'ar' ? `تم. ${label}.` : `Noted. ${label}.`;
}

/** The proactive line when the reminder is due: "Reminder: call the dentist." */
export function reminderLine(what: string, lang: Lang): string {
  const body = what.replace(/[.。]+$/u, '');
  if (/^\s*(?:تذكير|reminder)(?=[\s:：]|$)/iu.test(body)) return `${body.replace(/[.。]+$/u, '')}.`;
  return lang === 'ar' ? `تذكير: ${body}.` : `Reminder: ${body}.`;
}
